import { FC, Fragment, SetStateAction, useState } from "react";
import {
  IGroupPriceDBModel,
  IGroupPriceEditDBModel,
} from "../../../../model/DbMdel/groupDBModel";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { editGroupPrice } from "../../../../services/groups";
import toast from "react-hot-toast";
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import InputNumber from "rc-input-number";
import { Minus, Plus } from "react-feather";
import "@styles/react/libs/input-number/input-number.scss";
import LoadingButton from "../../../common/LoadingButton";
import SwalFire from "../../../common/Swal";
import LocationDropdown from "../../../locationForPage";

interface IGroupEditModalProps {
  setShowEdit: (value: SetStateAction<boolean>) => void;
  showEdit: boolean;
  group: IGroupPriceDBModel;
  onReload: () => void;
}

const GroupPriceEditModal: FC<IGroupEditModalProps> = ({
  setShowEdit,
  showEdit,
  group,
  onReload,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const AddStudentSchema = yup.object().shape({
    name: yup
      .string()
      .min(2, "Grup fiyat başlığı zorunlu alandır")
      .max(64, "Grup fiyat başlığı en fazla 64 karakter olmalıdır")
      .matches(
        /^([A-Za-z\ıİğĞşŞ\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s0-9]*)$/gi,
        "Geçerli bir grup fiyat başlığı giriniz"
      )
      .required("Grup fiyat başlığı zorunlu alandır"),

    maxusercount: yup
      .number()
      .min(2, "En az 2 kişi olmalıdır.")
      .max(999, "Maksimum sayıya ulaştınız!")
      .required("Kişi sayısı zorunlu alandır"),
    peruserprice: yup
      .number()
      .typeError("Geçerli bir sayı giriniz")
      .positive("Sıfırdan büyük bir sayı giriniz")
      .required("Kişi başına fiyat zorunlu alandır"),
  });

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(AddStudentSchema),
    defaultValues: {
      name: group.name,
      maxusercount: group.maxUserCount,
      peruserprice: group.perUserAndHourPrice,
      location: {
        id: group.location?.id,
        value: group.location?.id,
        label: group.location?.name,
      },
    },
  });

  const onSubmit = async (e: any) => {
    SwalFire({
      title: "Emin misiniz?",
      text: "Bir grup fiyat listesi düzenlemek üzeresiniz. İşleme devam etmek istiyor musunuz?",
      onOk: () => {
        const groupId = group.id;
        const groupp: IGroupPriceEditDBModel = {
          name: e.name,
          maxUserCount: e.maxusercount,
          perUserAndHourPrice: e.peruserprice,
          locationId: e.location.id,
        };
        setIsLoading(true);
        editGroupPrice(groupp, groupId)
          .then((res) => {
            toast.success("Grup Fiyatı Düzenleme Başarılı.");
            onReload();
            setShowEdit(false);
            setIsLoading(false);
            reset({
              name: "",
              maxusercount: 0,
              peruserprice: 0,
              location: {},
            });
          })
          .catch((err) => {
            toast.error(err);
            setIsLoading(false);
          });
      },
    });
  };
  return (
    <Fragment>
      <Modal
        isOpen={showEdit}
        toggle={() => setShowEdit(!showEdit)}
        className="modal-dialog-centered modal-lg"
      >
        <ModalHeader
          className="bg-transparent"
          toggle={() => setShowEdit(!showEdit)}
        ></ModalHeader>
        <ModalBody className="px-sm-5 mx-50 pb-5">
          <div className="text-center mb-2">
            <h1 className="mb-1"> Grup Fiyatı Düzenle</h1>
          </div>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row tag="form" className="gy-1 pt-75">
              <Col md={12} xs={12}>
                <Label>Lokasyon</Label>
                <LocationDropdown
                  control={control}
                  onChange={(event) => setValue("location", event)}
                  defaultValue={getValues("location")}
                />
              </Col>

              <Col md={12} xs={12}>
                <Label className="form-label" for="name">
                  Grup Fiyat Başlığı
                </Label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => {
                    return (
                      <Input
                        {...field}
                        innerRef={register("name").ref}
                        id="name"
                        placeholder="Grup fiyat başlığı"
                        invalid={errors.name && true}
                        type="text"
                        maxLength={64}
                        onKeyDown={(e) =>
                          e.key === "Enter" && e.preventDefault()
                        }
                      />
                    );
                  }}
                />
                {errors.name && (
                  <FormFeedback>{errors.name.message?.toString()}</FormFeedback>
                )}
              </Col>
              <Col lg={6} md={6} xs={12}>
                <Label className="form-label" for="maxusercount">
                  Kişi Sayısı
                </Label>
                <Controller
                  name="maxusercount"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputNumber
                        {...field}
                        defaultValue={2}
                        min={2}
                        max={999}
                        id="maxusercount"
                        upHandler={<Plus />}
                        downHandler={<Minus />}
                      />
                    );
                  }}
                />
                {errors.maxusercount && (
                  <FormFeedback>
                    {errors.maxusercount.message?.toString()}
                  </FormFeedback>
                )}
              </Col>
              <Col lg={6} md={6} xs={12}>
                <Label className="form-label" for="peruserprice">
                  Kişi Başı Ücret
                </Label>
                <InputGroup>
                  <Controller
                    name="peruserprice"
                    control={control}
                    render={({ field }) => {
                      return (
                        <Input
                          // type="number"
                          {...field}
                          innerRef={register("peruserprice").ref}
                          id="peruserprice"
                          invalid={errors.peruserprice && true}
                          placeholder="Kişi başı ücret"
                        />
                      );
                    }}
                  />
                  <InputGroupText>TL</InputGroupText>
                  {errors.peruserprice && (
                    <FormFeedback>
                      {errors.peruserprice.message?.toString()}
                    </FormFeedback>
                  )}
                </InputGroup>
              </Col>
              <Col xs={12} className="text-center mt-2 pt-50">
                <LoadingButton
                  className="me-1"
                  color="warning"
                  onClick={handleSubmit(onSubmit)}
                  form="hook-form"
                  loading={isLoading}
                >
                  Kaydet
                </LoadingButton>
                <Button
                  type="reset"
                  color="secondary"
                  outline
                  onClick={() => setShowEdit(false)}
                >
                  İptal
                </Button>
              </Col>
            </Row>
          </Form>
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default GroupPriceEditModal;
